/**
 * Helpers for video URL detection (YouTube, Vimeo, MP4) and Vimeo duration via oEmbed.
 * Used by Add/Edit video forms and learner VideoView/FreeVideoView.
 */

/**
 * Returns true if the URL is a playable embed (YouTube, Vimeo, or direct MP4).
 * Used to decide whether to show ReactPlayer inline vs "Link" button.
 */
export function isPlayableEmbed(url) {
  if (!url || typeof url !== "string") return false;
  const u = url.trim().toLowerCase();
  const isYoutube =
    u.includes("youtube") || u.includes("youtu.be") || u.includes("youtube-nocookie");
  const isVimeo = u.includes("vimeo.com");
  const isMp4 = u.endsWith(".mp4") || u.includes(".m4v");
  return isYoutube || isVimeo || isMp4;
}

/**
 * Extracts the numeric Vimeo video ID from a Vimeo URL (handles query params).
 */
export function getVimeoId(url) {
  if (!url || typeof url !== "string") return "";
  const clean = url.trim();
  if (clean.includes("player.vimeo.com/video/")) {
    const part = clean.split("/video/")[1];
    if (!part) return "";
    const segment = part.split("?")[0].split("&")[0].split("/")[0];
    return /^\d+$/.test(segment) ? segment : "";
  }
  if (clean.includes("vimeo.com/")) {
    const after = clean.split("vimeo.com/")[1];
    if (!after) return "";
    const segment = after.split("?")[0].split("#")[0].split("/")[0];
    return /^\d+$/.test(segment) ? segment : "";
  }
  return "";
}

/**
 * Normalizes embed URL for sending to backend. Vimeo URLs with query params (e.g. ?fl=ip&fe=ec)
 * become clean player URLs so the server never receives a mangled value.
 */
/** Returns URL as-is for save. No conversion (both vimeo.com and player.vimeo.com work). */
export function normalizeEmbedUrlForSave(url) {
  if (!url || typeof url !== "string") return url;
  return url.trim();
}

/**
 * Fetches Vimeo video duration (seconds) via oEmbed. Returns a promise that resolves
 * to a number or null. Use when ReactPlayer does not fire onDuration for Vimeo.
 */
export function getVimeoDuration(url) {
  if (!url || typeof url !== "string") return Promise.resolve(null);
  const trimmed = url.trim();
  if (!trimmed.includes("vimeo.com")) return Promise.resolve(null);
  const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(trimmed)}`;
  return fetch(oembedUrl)
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => (data && typeof data.duration === "number" ? data.duration : null))
    .catch(() => null);
}
